import type {
	ISbConfig,
	ISbStoriesParams,
	ISbStoryParams,
} from "gatsby-source-storyblok";
import { fixStoryblokLanguage } from "../../utils/storyblok/translations";

type StoryblokSourceConfig = {
	accessToken: string;
	version?: ISbStoryParams["version"];
	resolveRelations?: ISbStoryParams["resolve_relations"];
	resolveLinks?: ISbStoryParams["resolve_links"];
	includeTags?: boolean;
	includeDatasources?: string[];
	includeLinks?: boolean;
	languages?: string[];
	apiOptions?: ISbConfig;
	localAssets?: boolean;
};

const convertConfig = (storiesConfig: ISbStoriesParams): ISbStoryParams => {
	const { resolve_links, ...rest } = storiesConfig;

	return {
		...rest,
		// Link resolution is not supported for single story requests
		resolve_links: resolve_links === "link" ? undefined : resolve_links,
	};
};

const getStoryblokStoriesConfig = (): ISbStoriesParams => {
	const sourceOptions = getStoryblokSourceOptions();
	return {
		version: sourceOptions.version,
		language: sourceOptions.languages?.[0],
		resolve_relations: sourceOptions.resolveRelations,
		resolve_links: sourceOptions.resolveLinks,
	};
};

export const getStoryblokStoryConfig = (): ISbStoryParams =>
	convertConfig(getStoryblokStoriesConfig());

export const getStoryblokLanguage = (): string => {
	if (!process.env.GATSBY_LANGUAGE) {
		throw new Error("GATSBY_LANGUAGE is not defined");
	}

	return fixStoryblokLanguage(process.env.GATSBY_LANGUAGE);
};

export const getStoryblokSourceOptions = (): StoryblokSourceConfig => {
	const storyblokLanguage = getStoryblokLanguage();

	let redirectDatasourceSlug = process.env.GATSBY_LANGUAGE?.toLowerCase();

	if (redirectDatasourceSlug === "en" && process.env.GATSBY_STORE === "eu") {
		redirectDatasourceSlug = "en-eu";
	} else if (
		redirectDatasourceSlug === "en-gb" &&
		process.env.GATSBY_STORE === "uk"
	) {
		redirectDatasourceSlug = "en-gb-uk";
	}

	return {
		accessToken: process.env.GATSBY_STORYBLOK_TOKEN || "",
		version:
			process.env.GATSBY_STORYBLOK_STATUS === "draft" ? "draft" : "published",
		resolveRelations: ["product_page.accessories", "product_variant.material"],
		resolveLinks: "story",
		includeLinks: true,
		includeTags: false,
		includeDatasources: redirectDatasourceSlug
			? [redirectDatasourceSlug]
			: undefined,
		languages: [storyblokLanguage],
		apiOptions: {
			maxRetries: 10,
		},
	};
};
